/**
 * Checks element has class
 */
export function hasClass(element: Element, className: string): boolean {
    return element.classList.contains(className);
}

/**
 * Adds class to dom element
 */
export function addClass(element: Element, className: string): void {
    element.classList.add(className);
}

/**
 * Toggles class on dom element
 */
export function toggleClass(element: Element, className: string): void {
    hasClass(element, className)
        ? removeClass(element, className)
        : addClass(element, className);
}

/**
 * Removes class from dom element
 */
export function removeClass(element: Element, className: string): void {
    element.classList.remove(className);
}

/**
 * Starting with the Element itself, the closest method traverses parents (heading toward the document root) of the Element
 * until it finds a node that matches the provided selectorString.
 * Will return itself or the matching ancestor. If no such element exists, it returns null.
 */
export function closest(element: Element, selector: string): Element|null {
    return element.closest(selector);
}

/**
 * Remove passed attribute, if it is present.
 */
export function removeAttributeIfExists(element: Element, attribute: string): void {
    if (element.hasAttribute(attribute)) {
        element.removeAttribute(attribute);
    }
}

/**
 * Returns attribute value, or default if isn't present
 */
export function getAttributeOrDefault(element: Element, attribute: string, defaultVal: any = null): string|any {
    let value = defaultVal;

    if (element.hasAttribute(attribute)) {
        value = element.getAttribute(attribute);
    }

    return value;
}

export function outerHeight(element: HTMLElement, withMargin: boolean = false): number {
    const { offsetHeight } = element;
    if (!withMargin) {
        return offsetHeight;
    }

    const { marginTop, marginBottom } = getComputedStyle(element);
    return parseInt(marginTop === null ? '0' : marginTop)
        + offsetHeight
        + parseInt(marginBottom === null ? '0' : marginBottom);
}

export function elementExists(...selectors: string[]): boolean {
    for (const s of selectors) {
        if (document.querySelector(s) !== null) {
            return true;
        }
    }

    return false;
}

export function wrap(child: HTMLElement, container: HTMLElement): void {
    if (child.parentNode !== null) {
        child.parentNode.insertBefore(container, child);
    }

    container.appendChild(child);
}

export function unwrap(element: HTMLElement): void {
    if (element.parentNode === null) {
        return;
    }
    const deleteContainer = element.parentNode;
    const newContainer = deleteContainer.parentNode;

    if (newContainer === null) {
        return;
    }
    newContainer.insertBefore(element, deleteContainer);
    newContainer.removeChild(deleteContainer);
}

export function parents(element: HTMLElement, selector?: string): HTMLElement[] {
    const parents = [];

    for (; element && element !== document.documentElement; element = <HTMLElement>element.parentElement) {
        if (!selector || element.matches(selector)) {
            parents.push(element);
        }
    }

    return parents;
}

export function nextAll(element: HTMLElement, selector?: string): HTMLElement[] {
    let isNextAll = false;
    if (element.parentElement === null) {
        return [];
    }

    return <HTMLElement[]>Array.from(element.parentElement.children)
        .filter(sibling => {
            if (sibling.previousElementSibling === element) {
                isNextAll = true;
            }
            if (typeof selector !== 'undefined') {
                return sibling.matches(selector) && isNextAll;
            }

            return isNextAll;
        });
}

export function isVisible(element: Element): boolean {
    const { visibility, display } = getComputedStyle(element);

    return visibility === 'visible' && display !== 'none';
}
